// project/src/components/facilities/venues-api/VenueCard.tsx

import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { VenueApiResult } from '../../../types/venue';
import { VenuePhoto } from './components/VenueCard/VenuePhoto';
import { VenueHeader } from './components/VenueCard/VenueHeader';
import { VenueContent } from './components/VenueCard/VenueContent';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '../../ui/button';
import { UserPlus, Check, Info, Send } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface VenueCardProps {
  venue: VenueApiResult;
  distance?: number;
}

const VenueCard: React.FC<VenueCardProps> = ({ venue, distance }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [clubId, setClubId] = useState<string | null>(null);
  const { user } = useAuth();
  const [isMember, setIsMember] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [showApplicationTooltip, setShowApplicationTooltip] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);

  // Fetch the matching club_id once on mount
  useEffect(() => {
    const fetchClubId = async () => {
      try {
        const sanitized = venue.name
          .replace(/['"]/g, '')             // strip quotes
          .replace(/\s*\([^)]*\)\s*/g, '')  // strip parentheses + contents
          .trim();

        // 1) exact match on full or sanitized name
        const { data: exact, error: exactError } = await supabase
          .from('clubs')
          .select('club_id')
          .or(`club_name.eq.${venue.name},club_name.eq.${sanitized}`)
          .maybeSingle();  // <-- changed here

        if (exactError) {
          console.error('Supabase exact-club lookup error:', exactError);
        }

        if (exact?.club_id) {
          setClubId(exact.club_id);
          return;
        }

        // 2) fuzzy fallback
       // 2) fuzzy fallback: grab only the first matching row
const { data: fuzzyList, error: fuzzyError } = await supabase
  .from('clubs')
  .select('club_id')
  .ilike('club_name', `%${sanitized}%`)
  .limit(1);

if (fuzzyError) {
  console.error('Supabase fuzzy-club lookup error:', fuzzyError);
} else if (Array.isArray(fuzzyList) && fuzzyList.length > 0) {
  setClubId(fuzzyList[0].club_id);
}

      } catch (err) {
        console.error('Unexpected error fetching club ID:', err);
      }
    };

    fetchClubId();
    
    // Check if user has chosen not to see the tooltip again
    const dontShowAgainSetting = localStorage.getItem('dontShowJoinClubTooltip');
    if (dontShowAgainSetting === 'true') {
      setDontShowAgain(true);
    }
  }, [venue.name]);

  // Check if user is already a member of this club
  useEffect(() => {
    const checkMembership = async () => {
      if (!user || !clubId) return;
      
      try {
        // Get user's database ID
        const { data: userData } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!userData) return;

        // Check membership
        const { data: membershipData } = await supabase
          .from('user_clubs')
          .select('*') 
          .eq('user_id', userData.id) 
          .eq('club_id', clubId) 
          .maybeSingle();

        setIsMember(!!membershipData);
        
        // Check if user has already applied
        const { data: applicationData } = await supabase
          .from('club_applications')
          .select('*')
          .eq('user_id', userData.id)
          .eq('club_id', clubId)
          .maybeSingle();
          
        setHasApplied(!!applicationData);
      } catch (error) {
        console.error('Error checking membership:', error);
      }
    };

    checkMembership();
  }, [clubId, user]);

  // Use the fetched clubId directly
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clubId) {
      navigate(`/dashboard/clubs/${clubId}`);
    } else {
      console.warn(`No matching club found for: "${venue.name}"`);
    }
  };
  
  const handleAddMembership = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || !clubId) return;
    
    setIsAddingMember(true);
    try {
      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;
      
      // Add club membership
      const { error: membershipError } = await supabase
        .from('user_clubs')
        .insert({
          user_id: userData.id,
          club_id: clubId,
          is_primary: false
        });

      if (membershipError) throw membershipError;
      
      setIsMember(true);
    } catch (error) {
      console.error('Error adding club membership:', error);
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleAddMembershipClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (dontShowAgain) {
      handleAddMembership(e);
    } else {
      setShowTooltip(true);
    }
  };
  
  const handleConfirmJoin = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(false);
    handleAddMembership(e);
    
    if (dontShowAgain) {
      localStorage.setItem('dontShowJoinClubTooltip', 'true');
    }
  };
  
  const handleDontShowAgainChange = (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked) {
      localStorage.setItem('dontShowJoinClubTooltip', 'true');
    } else {
      localStorage.removeItem('dontShowJoinClubTooltip');
    }
  };
  
  const handleApplyToClub = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user || !clubId) return;
    
    setIsApplying(true);
    try {
      // Get user's database ID
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_id', user.id)
        .single();

      if (userError) throw userError;
      
      // Add club application
      const { error: applicationError } = await supabase
        .from('club_applications')
        .insert({
          user_id: userData.id,
          club_id: clubId,
          status: 'pending',
          application_date: new Date().toISOString()
        });

      if (applicationError) throw applicationError;
      
      setHasApplied(true);
    } catch (error) {
      console.error('Error applying to club:', error);
    } finally {
      setIsApplying(false);
      setShowApplicationTooltip(false);
    }
  };

  // Intersection observer for styling
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.5 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, []);

  // Copy address handler
  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(venue.address);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  return (
    <div
      ref={cardRef}
      className={cn(
        "flex flex-col md:flex-row bg-dark-200 rounded-xl overflow-hidden",
        "border transition-all duration-500",
        isVisible
          ? "border-gold-400 shadow-lg shadow-dark-900/5 scale-[1.01]"
          : "border-dark-300 scale-100",
        "hover:border-gold-400 hover:shadow-xl hover:shadow-dark-900/5 hover:scale-[1.01]"
      )}
    >
      <VenuePhoto venue={venue} />

      <div className="flex-1 p-3">
        <VenueHeader
          name={venue.name}
          address={venue.address}
          distance={distance}
          logo={venue.logo}
          onClick={handleClick}
          onCopyAddress={handleCopyAddress}
        />

        <VenueContent venue={venue} />
        {clubId && (
          <div className="mt-3 flex gap-2">
            {/* I'm a Member Button */}
            <Popover open={showTooltip} onOpenChange={setShowTooltip}>
              <PopoverTrigger asChild>
                <Button
  onClick={isMember ? undefined : handleAddMembershipClick}
  disabled={isMember || isAddingMember}
  className={cn(
    "flex items-center justify-center gap-2 flex-1",
    "px-4 py-2 rounded-lg transition-colors",
    (isMember || isAddingMember) && "!opacity-100",
    isMember 
      ? "bg-green-500 text-dark-500 border border-green-500/20" 
      : "bg-dark-300 hover:bg-dark-400 text-white"
  )}
>
                  {isAddingMember ? (
                    <div className="h-4 w-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
                  ) : isMember ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Member</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      <span>I'm a Member</span>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-80 p-4 bg-dark-200 border-dark-300 text-white"
                align="center"
              >
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-gold-400 flex-shrink-0 mt-0.5" />
                  <div className="space-y-3">
                    <p className="text-sm">
                      We trust you that you are a real member and will add you temporarily pending verification by the club admin or by other club members.
                    </p>
                    <div className="flex items-center space-x-2">
                      <Switch 
                        id="dont-show-again" 
                        checked={dontShowAgain} 
                        onCheckedChange={handleDontShowAgainChange}
                      />
                      <Label htmlFor="dont-show-again" className="text-sm text-dark-800">
                        Don't show this again
                      </Label>
                    </div>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowTooltip(false)}
                        className="border-dark-400"
                      >
                        Cancel
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleConfirmJoin}
                        className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                      >
                        Confirm
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Apply Button */}
            <Popover open={showApplicationTooltip} onOpenChange={setShowApplicationTooltip}>
              <PopoverTrigger asChild>
                <Button
                  onClick={() => !hasApplied && setShowApplicationTooltip(true)}
                  disabled={hasApplied || isApplying || isMember}
                  className={cn(
                    "flex items-center justify-center gap-2 flex-1",
                    "px-4 py-2 rounded-lg transition-colors",
                    hasApplied
                      ? "bg-blue-500/20 text-blue-500 border border-blue-500/20"
                      : "bg-gold-400 hover:bg-gold-500 text-dark-50"
                  )}
                >
                  {isApplying ? (
                    <div className="h-4 w-4 border-2 border-t-transparent border-dark-50 rounded-full animate-spin" />
                  ) : hasApplied ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Applied</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Apply</span>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent 
                className="w-80 p-4 bg-dark-200 border-dark-300 text-white"
                align="center"
              >
                {hasApplied ? (
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm">
                        Thank you for your application! We will review it and reach out with next steps.
                      </p>
                      <Button 
                        size="sm" 
                        onClick={() => setShowApplicationTooltip(false)}
                        className="bg-gold-400 hover:bg-gold-500 text-dark-50 mt-3 w-full"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-gold-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-3">
                      <p className="text-sm">
                        Submit an application to join this club. We'll review your application and reach out with next steps.
                      </p>
                      <div className="flex justify-end gap-2 mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setShowApplicationTooltip(false)}
                          className="border-dark-400"
                        >
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={handleApplyToClub}
                          className="bg-gold-400 hover:bg-gold-500 text-dark-50"
                        >
                          Submit Application
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueCard;